export interface IDeviceLocation {
  latitude: number;
  longitude: number;
}

/**
 * DENIED: location access was refused, by the person or by a setting.
 * UNAVAILABLE: access is fine, but no position was found in time.
 */
export type LocationFailure = 'DENIED' | 'UNAVAILABLE';

export type LocationResult = {ok: true; location: IDeviceLocation} | {ok: false; failure: LocationFailure};

const PERMISSION_DENIED = 1; // GeolocationPositionError.PERMISSION_DENIED

const POSITION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  // A check-in says where somebody is now, so an old fix will not do.
  maximumAge: 30 * 1000,
  timeout: 20 * 1000,
};

const getAndroidPermissions = (): any => {
  const cordova = (globalThis as any).cordova;
  if (!cordova || cordova.platformId !== 'android') {
    return undefined;
  }
  return cordova.plugins && cordova.plugins.permissions ? cordova.plugins.permissions : undefined;
}

const hasAndroidPermission = (permissions: any, permission: string): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    permissions.checkPermission(
      permission,
      (status: any) => resolve(!!(status && status.hasPermission)),
      (error: any) => reject(error)
    );
  });
}

/**
 * Android's WebView only hands out positions once the app itself holds a location permission, and
 * Cordova never asks for one on the page's behalf.
 *
 * Both permissions go in one request so that Android 12 and later offer their precise or approximate
 * choice, or offer to upgrade an earlier approximate grant. The plugin only reports whether both were
 * granted, but an approximate location still makes a check-in, so coarse access is enough to go on.
 */
const ensureAndroidLocationPermission = async (permissions: any): Promise<boolean> => {
  const fine = permissions.ACCESS_FINE_LOCATION || 'android.permission.ACCESS_FINE_LOCATION';
  const coarse = permissions.ACCESS_COARSE_LOCATION || 'android.permission.ACCESS_COARSE_LOCATION';
  if (await hasAndroidPermission(permissions, fine)) {
    return true;
  }
  await new Promise((resolve, reject) => permissions.requestPermissions([coarse, fine], resolve, reject));
  return hasAndroidPermission(permissions, coarse);
}

const readPosition = (): Promise<LocationResult> => {
  const geolocation = typeof navigator !== 'undefined' ? navigator.geolocation : undefined;
  if (!geolocation || typeof geolocation.getCurrentPosition !== 'function') {
    return Promise.resolve<LocationResult>({ok: false, failure: 'UNAVAILABLE'});
  }

  return new Promise<LocationResult>((resolve) => {
    geolocation.getCurrentPosition(
      (position) => resolve({ok: true, location: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      }}),
      (error) => resolve({ok: false, failure: error && error.code === PERMISSION_DENIED ? 'DENIED' : 'UNAVAILABLE'}),
      POSITION_OPTIONS
    );
  });
}

/**
 * Where the device is right now, asking for location access first when it has to. Never rejects.
 *
 * iOS and browsers ask for access themselves the first time a page reads the position.
 */
export const getCurrentLocation = async (): Promise<LocationResult> => {
  const permissions = getAndroidPermissions();
  if (permissions) {
    try {
      if (!(await ensureAndroidLocationPermission(permissions))) {
        return {ok: false, failure: 'DENIED'};
      }
    } catch (_) {
      // The WebView still gets asked below, and reports a missing permission as a denial.
    }
  }
  return readPosition();
}



// WEBPACK FOOTER //
// ./src/helpers/locationHelper.ts